<script>
import { addMonths, addMinutes, addSeconds, setHours, setMinutes, startOfSecond } from 'date-fns';
import { slide } from 'svelte/transition';
import Calendar from './Calendar.svelte';
import TimePicker from './TimePicker.svelte';
import { today, minute, getNow } from '../data/datetime';
import { getUser } from '../data/user';
import { router } from '../libs/router';
import { uuid } from '../libs/uuid';
import { createSprint } from '../data/sprints';
import { createEditor } from '../editor';
import { tooltipTop } from '../tooltip';
import globals from '../globals';

const personTip = `Start your own personal sprint right now!`;
const groupTip = `Schedule a group sprint within the next 30 days to sprint with others!`;
const privateTip = `Only those with the link can join this sprint.`;

const ERRORS = {
  DATA_REQUIRED: 'The request was empty.',
  INVALID_START_MINUTE: 'The start time for this sprint is invalid.',
  INVALID_END_MINUTE: 'The end time for this sprint is invalid.',
  NOT_FOUND: 'This could not be found.',
  FORBIDDEN: 'You do not have permission to do that.',
  AUTHORIZATION_REQUIRED: 'Your account was not created successfully. Refresh and try again.',
};

let personal;
let day = $today;
let start = $minute;
let end;
let duration = parseInt(localStorage.lastDuration) || 15;
let titleValue = '';
let listed = false;
let promptElement;
let promptEditor;

$: start = getTimeFromDay(day);
$: end = addMinutes(start, duration);
$: createPromptEditor(promptElement)


function createPromptEditor(root) {
  if (promptEditor) promptEditor.view.destroy();
  if (root) promptEditor = createEditor(root);
}

async function createNewSprint() {
  localStorage.lastDuration = duration;
  if (personal) {
    start = addSeconds(startOfSecond(getNow()), 6);
    end = addMinutes(start, duration);
    const sprint = await createSprint({ start: start.getTime(), end: end.getTime() });
    router.navigate('/sprints/' + sprint.id);
  } else {
    const user = await getUser();
    if (!user) return;
    const creator = user.uid;
    let text = titleValue.trim();
    const title = text ? text : undefined;
    text = promptEditor.getText().trim();
    const prompt = text ? promptEditor.getContents() : undefined;

    try {
      const sprint = await createSprint({ start: start.getTime(), end: end.getTime(), creator, title, prompt, listed });
      router.navigate('/sprints/' + sprint.id);
      globals.alert('Sprint Created!', 'To invite others to your sprint, share the URL/link with them!');
    } catch (err) {
      globals.alert('Error', ERRORS[err.message] || err.message);
    }
  }
}

function getTimeFromDay(day) {
  if (!day || !start) return;
  const hours = start.getHours();
  const minutes = start.getMinutes();
  return setMinutes(setHours(day, hours), minutes);
}

</script>

<div class="creator">
  <div class="toggle">
    <button
      class="btn large"
      class:primary={personal === undefined}
      class:outline={personal !== true}
      use:tooltipTop={personTip}
      on:click={() => personal = personal === true ? undefined : true}
    >Personal</button>
    <button
      class="btn large"
      class:primary={personal === undefined}
      class:outline={personal !== false}
      use:tooltipTop={groupTip}
      on:click={() => personal = personal === false ? undefined : false}
    >Group</button>
  </div>

  {#if personal === false}
  <div class="group-sprint" transition:slide|local={{ duration: 200 }}>
    <div class="directions">
      <p>Once created, you will only be able to <strong>change</strong> this sprint from <strong>this device</strong>.</p>
    </div>
    <div class="form-group">
      <input type="text" class="form-control" placeholder="Title (optional)" bind:value={titleValue}/>
    </div>
    <div class="form-group">
      <div class="prompt form-control" bind:this={promptElement} placeholder="Writing prompt (optional)"></div>
    </div>
    <div class="date-time">
      <div class="calendar">
        <div class="time-label">on</div>
        <div class="form-group">
          <Calendar bind:selected={day} min={$today} max={addMonths($today, 1)}/>
        </div>
      </div>
      <div class="time">
        <div class="time-label">at</div>
        <div class="form-group">
          <TimePicker min={addMinutes($minute, 1)} bind:selected={start} interval={5}/>
        </div>
        <div class="time-label">for</div>
        <div class="form-group">
          <select class="form-control" bind:value={duration}>
            <option value={5}>5 minutes</option>
            <option value={10}>10 minutes</option>
            <option value={15}>15 minutes</option>
            <option value={20}>20 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={45}>45 minutes</option>
            <option value={60}>1 hour</option>
          </select>
        </div>
        <div class="time-label">will be</div>
        <div class="form-group">
          <label class="form-check-label" for="listed-check" use:tooltipTop={privateTip}>
            <i class="icon icon-checkbox-{listed ? 'marked' : 'blank'}-circle-outline"></i>
            <input class="form-check-input" type="checkbox" id="listed-check" bind:checked={listed}>
            Publicly Listed
          </label>
          <div class="hint">Hint: Share the sprint’s link for others to join.</div>
        </div>
        <div class="spacer"></div>
        <button class="btn primary schedule-btn" on:click={createNewSprint}>Schedule <i class="icon icon-chevron-right"></i></button>
      </div>
    </div>
  </div>
  {:else if personal}
  <div class="date-time personal" transition:slide|local={{ duration: 200 }}>
    <div class="time-only">
      <select class="form-control" bind:value={duration}>
        <option value={5}>5 minutes</option>
        <option value={10}>10 minutes</option>
        <option value={15}>15 minutes</option>
        <option value={20}>20 minutes</option>
        <option value={30}>30 minutes</option>
        <option value={45}>45 minutes</option>
        <option value={60}>1 hour</option>
      </select>
      <button class="btn primary" on:click={createNewSprint}>Start now <i class="icon icon-chevron-right"></i></button>
    </div>
  </div>
  {/if}
</div>

<style>
.creator {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.toggle {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}
.toggle .btn {
	width: 128px;
	margin: 0 8px;
}
.group-sprint {
  display: flex;
  flex-direction: column;
}
.directions {
  font-size: var(--font-size-100);
	color: var(--warm-grey-300);
	text-align: center;
}
.date-time {
  display: flex;
}
.date-time.personal {
  align-self: center;
}
.calendar {
  margin-right: 32px;
}
.time {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
}
.spacer {
  flex: 1;
}
.time-only .form-control {
  margin-right: 16px;
}
.time-only {
  display: flex;
}
.time-only .btn {
  white-space: nowrap;
}
.schedule-btn {
  align-self: stretch;
  margin-bottom: 16px;
}
.time-label {
  font-size: var(--font-size-100);
  color: var(--warm-grey-400);
  margin-bottom: 2px;
}
.form-check-label {
  position: relative;
  width: 100%;
  font-size: var(--font-size-200);
  cursor: pointer;
}
.form-check-label .icon {
  font-size: 24px;
  margin-left: -1.25rem;
  margin-right: .25rem;
  line-height: 0;
  position: relative;
  bottom: -3px;
}
.form-check-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}
.icon-checkbox-blank-circle-outline {
  color: var(--warm-grey-400);
}
.icon-checkbox-marked-circle-outline {
  color: var(--teal-600);
}
.hint {
  margin-top: 8px;
  font-size: var(--font-size-050);
  color: var(--warm-grey-400);
}
@media (max-width: 640px) {
  .date-time {
    flex-direction: column;
  }
}
</style>
